<script lang="ts">
  // "Show races for: <area>" — filters every Charleston list; remembered (localStorage) and in the URL.
  import type { AreaDef } from '../../types';
  import { app } from '../../lib/app.svelte';
  let { areas, used, area }: { areas: AreaDef[]; used: Set<string>; area: string | null } = $props();
  let trigger: HTMLButtonElement | undefined = $state();
  let search: HTMLInputElement | undefined = $state();
  let open = $state(false);
  let q = $state('');
  const GROUPS: [AreaDef['kind'], string][] = [['muni', 'Cities & towns'], ['region', 'Neighborhoods'], ['cc', 'County Council districts'], ['sh', 'S.C. House districts'], ['sb', 'School Board districts'], ['cd', 'Congressional districts']];
  const avail = $derived(areas.filter((a) => used.has(a.area_id) && a.kind !== 'county'));
  const current = $derived(avail.find((a) => a.area_id === area) ?? areas.find((a) => a.area_id === area));
  const QUICK = ['muni:mount_pleasant', 'region:west_ashley', 'muni:north_charleston', 'region:james_island', 'muni:charleston', 'region:peninsula', 'region:johns_island'];
  const quick = $derived(QUICK.map((id) => avail.find((a) => a.area_id === id)).filter((a): a is AreaDef => !!a).slice(0, 5));
  const filtered = $derived(GROUPS.map(([k, label]) => ({ label, items: avail.filter((a) => a.kind === k && a.label.toLowerCase().includes(q.trim().toLowerCase())).sort((a, b) => a.label.localeCompare(b.label, 'en', { numeric: true })) })).filter((g) => g.items.length));
  function pick(id: string | null) { app.setArea(id); close(); }
  function close() { const was = open; open = false; q = ''; if (was) trigger?.focus(); }
  // focus the search box when the list opens; Escape closes and returns focus to the button
  $effect(() => {
    if (!open) return;
    search?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); close(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });
</script>

<div class="picker">
  <div class="line">
    <span class="lbl">Show races for</span>
    <div class="anchor">
      <button class="pick" type="button" bind:this={trigger} aria-haspopup="dialog" aria-expanded={open} onclick={() => (open = !open)}>
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z" fill="none" stroke="currentColor" stroke-width="2" /><circle cx="12" cy="10" r="2.6" fill="currentColor" /></svg>
        <span>{current?.label ?? 'All of Charleston County'}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" /></svg>
      </button>
      {#if open}
        <div class="pop" role="dialog" aria-label="Choose an area">
          <input type="search" placeholder="Search areas…" bind:value={q} bind:this={search} aria-label="Search areas" />
          <button class="opt all" type="button" onclick={() => pick(null)} aria-pressed={!area}>All of Charleston County</button>
          {#each filtered as g}
            <div class="grp">{g.label}</div>
            {#each g.items as a (a.area_id)}<button class="opt" type="button" aria-pressed={area === a.area_id} onclick={() => pick(a.area_id)}>{a.label}</button>{/each}
          {/each}
        </div>
        <button class="scrim" type="button" aria-label="Close" tabindex="-1" onclick={close}></button>
      {/if}
    </div>
    {#if area}<button class="btn small ghost" type="button" onclick={() => pick(null)}>Clear</button>{/if}
  </div>
  {#if quick.length}
    <div class="chips" aria-label="Popular areas">
      {#each quick as a (a.area_id)}<button class="chip" type="button" aria-pressed={area === a.area_id} onclick={() => pick(area === a.area_id ? null : a.area_id)}>{a.label}</button>{/each}
    </div>
  {/if}
</div>

<style>
  .picker { display: flex; flex-direction: column; gap: 10px; }
  .line { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .lbl { font: 700 0.72rem/1 var(--font); letter-spacing: 0.12em; text-transform: uppercase; color: var(--ink-3); }
  .anchor { position: relative; }
  .pick { display: inline-flex; align-items: center; gap: 9px; border: 1.5px solid var(--ink); background: var(--card); color: var(--ink); border-radius: 999px; padding: 10px 16px; font: 600 1.02rem/1 var(--display); cursor: pointer; box-shadow: var(--shadow); }
  .pick:hover { background: var(--card-3); }
  .pop { position: absolute; top: calc(100% + 8px); left: 0; z-index: 62; width: min(360px, calc(100vw - 28px)); max-height: min(70vh, 520px); overflow-y: auto; background: var(--card); border: 1px solid var(--line); border-radius: 16px; box-shadow: var(--shadow-lg); padding: 10px; }
  .pop input { width: 100%; border: 1px solid var(--line-2); background: var(--card-2); border-radius: 10px; padding: 9px 12px; margin-bottom: 6px; }
  .grp { font: 700 0.68rem/1 var(--font); letter-spacing: 0.1em; text-transform: uppercase; color: var(--ink-3); padding: 12px 8px 6px; }
  .opt { display: block; width: 100%; text-align: left; border: 0; background: none; padding: 8px 10px; border-radius: 9px; cursor: pointer; font-size: 0.92rem; }
  .opt:hover { background: var(--card-3); }
  .opt[aria-pressed='true'] { background: var(--ink); color: var(--card); font-weight: 600; }
  .opt.all { font-weight: 600; }
  .scrim { position: fixed; inset: 0; z-index: 61; background: transparent; border: 0; cursor: default; }
  @media (max-width: 640px) {
    .pop { position: fixed; left: 8px; right: 8px; top: auto; bottom: calc(var(--scrubber-h) + 30px); width: auto; max-height: 62vh; }
    .scrim { background: rgba(0, 0, 0, 0.35); }
  }
</style>
